import React, { useState, useEffect } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Menu } from 'antd'
import { Link } from 'react-router'
import { withRouter } from 'router'
import classNames from 'classnames'
import localforage from 'myStore'
import { find } from 'lodash'
import styles from './style.module.scss'

interface WithRouterProps {
  location: {
    pathname: string
    search?: string
    hash?: string
    state?: any
  }
  history?: any
  params?: any
  routes?: any
}

interface MenuItem {
  key: string
  title: string
  url?: string
  icon?: string
  disabled?: boolean
  category?: boolean
  count?: number
  children?: MenuItem[]
  roles?: string[]
  target?: string
}

const mapStateToProps = ({ menu, appsettings, user }: any) => ({
  menuData: menu.menuData as MenuItem[],
  logo: appsettings.logo as string,
  menuColor: appsettings.menuColor as string,
  role: user.role as string,
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

interface MenuTopProps extends PropsFromRedux, WithRouterProps {}

const MenuTop: React.FC<MenuTopProps> = ({
  menuData = [],
  location: { pathname },
  menuColor,
  logo,
  role,
}) => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])

  useEffect(() => {
    const initSelectedKeys = async () => {
      const storedKeys = (await localforage.getItem('app.menu.selectedKeys')) as string[] | null
      setSelectedKeys(storedKeys || [])
    }
    initSelectedKeys()
  }, [])

  useEffect(() => {
    applySelectedKeys()
  }, [pathname, menuData])

  const applySelectedKeys = () => {
    const flattenItems = (items: MenuItem[], key: string): MenuItem[] =>
      items.reduce((flattenedItems: MenuItem[], item: MenuItem) => {
        flattenedItems.push(item)
        if (Array.isArray(item[key as keyof MenuItem])) {
          return flattenedItems.concat(flattenItems(item[key as keyof MenuItem] as MenuItem[], key))
        }
        return flattenedItems
      }, [])
    const selectedItem = find(flattenItems(menuData, 'children'), ['url', pathname])
    setSelectedKeys(selectedItem ? [selectedItem.key] : [])
  }

  const handleClick = async (e: any) => {
    await localforage.setItem('app.menu.selectedKeys', [e.key])
    setSelectedKeys([e.key])
  }

  const generateMenuItems = (): any => {
    const generateItem = (item: MenuItem) => {
      const { key, title, url, icon, disabled, count } = item
      if (item.category) {
        return null
      }
      if (item.url) {
        return (
          <Menu.Item key={key} disabled={disabled}>
            {item.target && (
              <a href={url} target={item.target} rel="noopener noreferrer">
                {icon && <span className={`${icon} ${styles.icon}`} />}
                <span className={styles.title}>{title}</span>
                {count && <span className="badge badge-success ml-2">{count}</span>}
              </a>
            )}
            {!item.target && (
              <Link to={url!}>
                {icon && <span className={`${icon} ${styles.icon}`} />}
                <span className={styles.title}>{title}</span>
                {count && <span className="badge badge-success ml-2">{count}</span>}
              </Link>
            )}
          </Menu.Item>
        )
      }
      return (
        <Menu.Item key={key} disabled={disabled}>
          {icon && <span className={`${icon} ${styles.icon}`} />}
          <span className={styles.title}>{title}</span>
          {count && <span className="badge badge-success ml-2">{count}</span>}
        </Menu.Item>
      )
    }
    const generateSubmenu = (items: MenuItem[]): any =>
      items.map((menuItem) => {
        if (menuItem.children) {
          const subMenuTitle = (
            <span key={menuItem.key}>
              {menuItem.icon && <span className={`${menuItem.icon} ${styles.icon}`} />}
              <span className={styles.title}>{menuItem.title}</span>
              {menuItem.count && <span className="badge badge-success ml-2">{menuItem.count}</span>}
            </span>
          )
          return (
            <Menu.SubMenu title={subMenuTitle} key={menuItem.key}>
              {generateSubmenu(menuItem.children)}
            </Menu.SubMenu>
          )
        }
        return generateItem(menuItem)
      })
    return menuData.map((menuItem) => {
      if (menuItem.roles && !menuItem.roles.includes(role)) {
        return null
      }
      if (menuItem.children) {
        const subMenuTitle = (
          <span key={menuItem.key}>
            {menuItem.icon && <span className={`${menuItem.icon} ${styles.icon}`} />}
            <span className={styles.title}>{menuItem.title}</span>
            {menuItem.count && <span className="badge badge-success ml-2">{menuItem.count}</span>}
          </span>
        )
        return (
          <Menu.SubMenu title={subMenuTitle} key={menuItem.key}>
            {generateSubmenu(menuItem.children)}
          </Menu.SubMenu>
        )
      }
      return generateItem(menuItem)
    })
  }

  return (
    <div
      className={classNames(`${styles.menu}`, {
        [styles.white]: menuColor === 'white',
        [styles.gray]: menuColor === 'gray',
        [styles.dark]: menuColor === 'dark',
      })}
    >
      <div className={styles.logoContainer}>
        <div className={styles.logo}>
          <img src="resources/images/logo.svg" className="mr-2" alt={logo} />
          <div className={styles.name}>{logo}</div>
          {/* logo === 'Clean UI Pro' && <div className={styles.descr}>React</div> */}
        </div>
      </div>
      <div className={styles.navigation}>
        <Menu onClick={handleClick} selectedKeys={selectedKeys} mode="horizontal">
          {generateMenuItems()}
        </Menu>
      </div>
    </div>
  )
}

// @ts-expect-error withRouter + connect type incompatibility
export default withRouter(connector(MenuTop))
